/* eslint-disable @typescript-eslint/no-empty-function */
import { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  IonButton,
  IonButtons,
  IonIcon,
  IonItem,
  IonSpinner,
  IonTitle,
} from '@ionic/react'
import { useMutation } from '@tanstack/react-query'
import { chevronBack } from 'ionicons/icons'

import { Avatar } from '@/components/atoms/Avatar'
import { Button } from '@/components/atoms/Button'
import { CharacterCount } from '@/components/atoms/CharacterCount'
import { TextArea } from '@/components/atoms/TextArea'
import { Title } from '@/components/atoms/Title'
import { UserName } from '@/components/atoms/UserName'
import { VideoThumbnailSelector } from '@/components/organisms/VideoThumbnailSelector'
import { Main } from '@/components/templates/main'

import { joinContest } from '@/services/contest'
import { errorBoundary } from '@/services/errorBoundary'
import { UserInfo } from '@/services/user'
import { useFileUpload } from '@/lib/hooks/useFileUpload'
import { validateMarkdownMaxLength } from '@/utils/formValidators'

interface IContestUploadVideo {
  onDismiss: () => void
  onComplete: () => void
  onModalComplete: () => void
  videoDataUrl: string
  thumbnailUrl: string
  contestId: string
  user: UserInfo
  thumbnailOffset?: number
  duration?: number
}

export const ContestUploadVideo: FC<IContestUploadVideo> = ({
  videoDataUrl,
  thumbnailUrl,
  thumbnailOffset = 0,
  duration = 0,
  contestId,
  user,
  onComplete,
  onModalComplete,
  onDismiss,
}) => {
  const { t } = useTranslation('common')
  const { upload, progressPercentage, isUploading, complete } = useFileUpload()

  const { mutate, isPending } = useMutation({
    mutationKey: ['joinContest'],
    mutationFn: joinContest,
    onError: errorBoundary,
  })

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid },
    watch,
  } = useForm({
    defaultValues: {
      description: '',
    },
    mode: 'all',
  })

  const descriptionValue = watch('description')
  const MAX_DESCRIPTION_LENGTH = 2_200

  const onSubmit = () => {
    if (!videoDataUrl) {
      return
    }

    const video = `users/${user.id}/videos/${contestId}`

    fetch(videoDataUrl)
      .then((res) => res.blob())
      .then((blob) =>
        upload({
          fileName: video,
          file: blob,
          metadata: {
            thumbnailOffset: String(thumbnailOffset),
            duration: String(duration),
          },
          description:
            getValues().description || t('contest.descriptionFallback'),
        })
      )
      .then(() => onModalComplete())
  }

  const header = (
    <>
      <IonButtons slot="secondary">
        <IonButton
          mode="md"
          className="round plain ion-color"
          onClick={() => onDismiss()}
        >
          <IonIcon
            slot="icon-only"
            className="text-primary"
            icon={chevronBack}
          />
        </IonButton>
      </IonButtons>
      <IonButtons slot="primary">
        <Button
          onClick={handleSubmit(onSubmit)}
          className="submit plain"
          isDisabled={!isValid || isUploading}
          mode="md"
        >
          {t('contest.create.saveVideo')}
        </Button>
      </IonButtons>
      <IonTitle>
        <Title className="edit-title" level={2}>
          {t('contest.create.joinContestTitle')}
        </Title>
      </IonTitle>
    </>
  )

  useEffect(() => {
    if (complete) {
      const { description } = getValues()
      mutate(
        {
          contestId,
          description,
        },
        {
          onSuccess: onComplete,
        }
      )
    }
  }, [complete, getValues, mutate, onComplete, contestId])

  return (
    <Main header={header}>
      <div className="flex min-h-full justify-center items-center p-4">
        <div className="text-center mb-8 w-full">
          {isPending || isUploading ? (
            <>
              <IonSpinner className="w-20 h-20 mb-4" color="main" />
              <b className="block w-full text-white">
                {t('contest.create.uploadingVideo')} <br />
                {`${progressPercentage ?? 0}%`}
              </b>
            </>
          ) : (
            <>
              <IonItem>
                <span className="pr-2">
                  <Avatar
                    userId={user?.id}
                    isAmbassador={user?.isAmbassador}
                    badge={user?.badge}
                  />
                </span>
                <UserName name={user.username} />
              </IonItem>
              <IonItem>
                <div className="w-full ml-[3rem]">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Controller
                      control={control}
                      name="description"
                      rules={{
                        validate: {
                          maxLength: validateMarkdownMaxLength(
                            MAX_DESCRIPTION_LENGTH
                          ),
                        },
                      }}
                      render={({ field }) => (
                        <TextArea
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.value)
                          }}
                          className="post w-full bg-dark"
                          placeholder={t('contest.captionPlaceholder')}
                        />
                      )}
                    />
                    <CharacterCount
                      currentInput={descriptionValue}
                      maxLength={MAX_DESCRIPTION_LENGTH}
                    />
                  </form>
                </div>
              </IonItem>
              <div className="flex-1 relative px-16 mt-4">
                <VideoThumbnailSelector
                  videoUrl={videoDataUrl}
                  thumbnailUrl={thumbnailUrl}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Main>
  )
}
